import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Label,
  CardTitle,
  Form,
  FormFeedback,
  Button,
  InputGroup,
  Input,
  Table,
  Spinner,
  Badge
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import { orderBy } from "lodash";
//Utils
import { formatDateString } from "helpers/util";
//Redux
import { getUsersList, updateAttendance, updateTimesheet, updatePenalty } from "store/userlist/action";
//Helpers
import { validate_attendance_sync } from "helpers/backend_helper";

const AttendanceDetails = () => {

  document.title = "Attendace Details | TenderSoftware - Flexi TracBoard";
  const dispatch = useDispatch();
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState("");
  const userList = useSelector(state => state?.userlist?.alluserslist?.data || []);
  const orderedUserLists = orderBy(userList, 'username');
  const allUserLists = [{ id: 0, label: 'All', value: 0 }].concat(orderedUserLists.map(el => ({ ...el, label: el.username, value: el.id })));
  const formik = useFormik({
    initialValues: {
      date: [],
      employees: [],
      updatePenalties: false,
      updateRemoteWork: false,
      submitAttendance: false,
      deleteExistingAttendance: false,
      skipTimesheet: false,
      submitTimesheet: false,
      updateNonReportPenalty: false
    },
    validationSchema: Yup.object({
      date: Yup.array().min(2, "Please select both start and end date"),
      employees: Yup.array().min(1, "At least one employee must be selected")
    }),
    onSubmit: (values) => {
      const postReq = {
        fromDate: formatDateString(values?.date[0]),
        toDate: formatDateString(values?.date[1]),
        employeeId: values?.employees[0]?.id === 0 ? [] : (values?.employees || []).map(el => el?.id),
        updatePenalties: values?.updatePenalties,
        updateRemoteWork: values?.updateRemoteWork,
        submitAttendance: values?.submitAttendance,
        deleteExistingAttendance: values?.deleteExistingAttendance
      }
      const handleResponse = () => {
          formik.resetForm();
          formik.setFieldValue('date', null)
      }
      dispatch(updateAttendance(postReq, handleResponse))
    }
  });

  useEffect(() => {
    dispatch(getUsersList());
  }, [dispatch]);

  const onChangeSelectors = (selectedOptions) => {
    const isAllLast = selectedOptions[selectedOptions.length - 1]?.value === 0;

    if (isAllLast) {
      formik.setFieldValue('employees', [allUserLists.find(option => option.value === 0)]);
    } else {
      formik.setFieldValue('employees', selectedOptions.filter(option => option.value !== 0));
    }
  };

  const handleUpdateTimesheet = () => {
    if (formik.values.date.length < 2 || formik.values.employees.length === 0) {
      formik.setTouched({ date: true, employees: true });
      return;
    }
    const postReq = {
      fromDate: formatDateString(formik.values.date[0]),
      toDate: formatDateString(formik.values.date[1]),
      employeeId: formik.values.employees[0]?.id === 0 ? [] : (formik.values.employees || []).map(el => el?.id),
      skipTimesheet: formik.values.skipTimesheet,
      updatePenalties: formik.values.updatePenalties,
      updateRemoteWork: formik.values.updateRemoteWork,
      submitTimesheet: formik.values.submitTimesheet,
      deleteExistingTimesheet: formik.values.deleteExistingAttendance
    }
    const handleResponse = () => {
      formik.resetForm();
      formik.setFieldValue('date', null)
    }
    dispatch(updateTimesheet(postReq, handleResponse))
  };

  const handleUpdatePenalty = () => {
    if (formik.values.date.length < 2) {
      formik.setTouched({ date: true });
      return;
    }
    const postReq = {
      Startdate: formatDateString(formik.values.date[0]),
      Enddate: formatDateString(formik.values.date[1])
    }
    const handleResponse = () => {
      formik.resetForm();
      formik.setFieldValue('date', null)
    }
    dispatch(updatePenalty(postReq, handleResponse))
  };

  const handleValidateSync = async () => {
    if (formik.values.date.length < 2) {
      formik.setTouched({ date: true });
      return;
    }
    const selected = (formik.values.employees || []).filter(el => el?.id !== 0);
    const postReq = {
      startdate: formatDateString(formik.values.date[0]),
      enddate: formatDateString(formik.values.date[1]),
      userId: selected.length === 1 ? String(selected[0].id) : "",
      skipAttendance: true,
      updatePenalties: formik.values.updatePenalties,
      updateRemoteWork: formik.values.updateRemoteWork,
      submitAttendance: formik.values.submitAttendance,
      deleteExistingAttendance: formik.values.deleteExistingAttendance
    };
    setSyncLoading(true);
    setSyncError("");
    setSyncResult(null);
    try {
      const res = await validate_attendance_sync(postReq);
      if (res?.validationResult) {
        setSyncResult(res.validationResult);
      } else {
        setSyncError(res?.message || "Validation failed. Please try again.");
      }
    } catch (err) {
      setSyncError("Validation failed. Please try again.");
    } finally {
      setSyncLoading(false);
    }
  };

  const differenceBadgeColor = (type) => {
    switch (type) {
      case "MissingInERP": return "danger";
      case "MissingInFlexiTrac": return "warning";
      case "TotalHoursMismatch": return "info";
      case "BillableHoursMismatch": return "primary";
      default: return "secondary";
    }
  };

  const formatHours = (value) =>
    value === null || value === undefined ? "—" : value;

  // Escape values so user data can't break the generated Excel/HTML markup.
  const escapeCell = (value) => {
    if (value === null || value === undefined) return "";
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  };

  // Builds an Excel-compatible workbook (SpreadsheetML/HTML table) entirely on
  // the client from the already-loaded syncResult — no API call required.
  const handleExportExcel = () => {
    if (!syncResult) return;

    const summaryRows = [
      ["FlexiTrac Records", syncResult.totalFlexiRecords],
      ["ERP Records", syncResult.totalERPRecords],
      ["Matched", syncResult.matchedCount],
      ["Missing in ERP", syncResult.missingInERPCount],
      ["Missing in FlexiTrac", syncResult.missingInFlexiTracCount],
      ["Total Hours Mismatch", syncResult.totalHoursMismatchCount],
      ["Billable Hours Mismatch", syncResult.billableHoursMismatchCount]
    ]
      .map(
        ([label, value]) =>
          `<tr><td>${escapeCell(label)}</td><td>${escapeCell(value)}</td></tr>`
      )
      .join("");

    const headers = [
      "Employee Id",
      "Employee Name",
      "Date",
      "Difference",
      "Flexi Total",
      "ERP Total",
      "Total Diff",
      "Flexi Billable",
      "ERP Billable",
      "Billable Diff",
      "Flexi Status",
      "ERP Status"
    ];

    const detailRows = (syncResult.differences || [])
      .map((row) => {
        const cells = [
          row.employeeId,
          row.employeeName,
          row.date ? String(row.date).substring(0, 10) : "",
          row.differenceType,
          row.flexiTotalHours,
          row.erpTotalHours,
          row.totalHoursDiff,
          row.flexiBillableHours,
          row.erpBillableHours,
          row.billableHoursDiff,
          row.flexiStatus,
          row.erpStatus
        ];
        return `<tr>${cells.map((c) => `<td>${escapeCell(c)}</td>`).join("")}</tr>`;
      })
      .join("");

    const html =
      `<html xmlns:o="urn:schemas-microsoft-com:office:office" ` +
      `xmlns:x="urn:schemas-microsoft-com:office:excel" ` +
      `xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="UTF-8"></head><body>` +
      `<table border="1"><tr><th colspan="2" style="text-align:left">Summary</th></tr>${summaryRows}</table>` +
      `<br/>` +
      `<table border="1"><thead><tr>${headers
        .map((h) => `<th>${escapeCell(h)}</th>`)
        .join("")}</tr></thead><tbody>${detailRows}</tbody></table>` +
      `</body></html>`;

    const blob = new Blob(["﻿", html], {
      type: "application/vnd.ms-excel;charset=utf-8"
    });
    const from = formik.values.date?.[0] ? formatDateString(formik.values.date[0]) : "";
    const to = formik.values.date?.[1] ? formatDateString(formik.values.date[1]) : "";
    const fileName = `AttendanceSyncValidation${from ? `_${from}_to_${to}` : ""}.xls`;

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row className="d-flex justify-content-center">
            <Col xl="6">
              <Card>
                <CardBody>
                  <CardTitle className="mb-4 text-center">Attendance Details</CardTitle>
                  <Form onSubmit={formik.handleSubmit}>
                    <div className="row mb-4">
                      <Label
                        htmlFor="date"
                        className="col-sm-3 col-form-label"
                      >
                        Date
                      </Label>
                      <Col sm={9} className="flex-grow-1">
                        <div className="mb-4">
                          <InputGroup>
                            <Flatpickr
                              className="form-control d-block"
                              placeholder="Please select date"
                              options={{
                                mode: "range",
                                dateFormat: "Y-m-d",
                                maxDate: new Date().toISOString().split('T')[0]
                              }}
                              value={formik.values.date}
                              onChange={(selectedDates) => formik.setFieldValue('date', selectedDates)}
                            />
                          </InputGroup>
                          {formik.errors.date && formik.touched.date ? (
                            <FormFeedback className="d-block text-sm">
                              {formik.errors.date}
                            </FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                    </div>
                    <div className="row mb-4">
                      <Label
                        htmlFor="employees"
                        className="col-sm-3 col-form-label"
                      >
                        Employees
                      </Label>
                      <Col sm={9}>
                        <Select
                          isMulti
                          options={allUserLists}
                          classNamePrefix="select2-selection"
                          value={formik.values.employees}
                          onChange={(selectedOptions) => onChangeSelectors(selectedOptions)}
                          placeholder="Please select employees"
                        />
                        {formik.errors.employees && formik.touched.employees ? (
                          <FormFeedback className="d-block">
                            {formik.errors.employees}
                          </FormFeedback>
                        ) : null}
                      </Col>
                    </div>
                    <div className="row mb-4">
                      <Label className="col-sm-3 col-form-label">
                        Options
                      </Label>
                      <Col sm={9}>
                        <div className="form-check mb-2">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            onChange={formik.handleChange}
                            defaultChecked={formik.values.updatePenalties}
                            id="updatePenalties"
                          />
                          <Label className="form-check-label" htmlFor="updatePenalties">
                            Update Penalties
                          </Label>
                        </div>
                        <div className="form-check mb-2">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            onChange={formik.handleChange}
                            defaultChecked={formik.values.updateRemoteWork}
                            id="updateRemoteWork"
                          />
                          <Label className="form-check-label" htmlFor="updateRemoteWork">
                            Update Remote Work
                          </Label>
                        </div>
                        <div className="form-check mb-2">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            onChange={formik.handleChange}
                            defaultChecked={formik.values.submitAttendance}
                            id="submitAttendance"
                          />
                          <Label className="form-check-label" htmlFor="submitAttendance">
                            Submit Attendance
                          </Label>
                        </div>
                        <div className="form-check mb-2">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            onChange={formik.handleChange}
                            defaultChecked={formik.values.deleteExistingAttendance}
                            id="deleteExistingAttendance"
                          />
                          <Label className="form-check-label" htmlFor="deleteExistingAttendance">
                            Delete Existing Attendance
                          </Label>
                        </div>
                        <div className="form-check mb-2">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            onChange={formik.handleChange}
                            defaultChecked={formik.values.skipTimesheet}
                            id="skipTimesheet"
                          />
                          <Label className="form-check-label" htmlFor="skipTimesheet">
                            Skip Timesheet
                          </Label>
                        </div>
                        <div className="form-check mb-2">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            onChange={formik.handleChange}
                            defaultChecked={formik.values.submitTimesheet}
                            id="submitTimesheet"
                          />
                          <Label className="form-check-label" htmlFor="submitTimesheet">
                            Submit Timesheet
                          </Label>
                        </div>
                        <div className="form-check mb-2">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            onChange={formik.handleChange}
                            defaultChecked={formik.values.updateNonReportPenalty}
                            id="updateNonReportPenalty"
                          />
                          <Label className="form-check-label" htmlFor="updateNonReportPenalty">
                            Update Non Report Penalty To ERP
                          </Label>
                        </div>
                      </Col>
                    </div>
                    <div className="row justify-content-end">
                      <Col sm={9}>
                        <div className="text-end">
                          <Button
                            type="submit"
                            color="primary"
                            className="w-md me-2"
                          >
                            Update Attendance
                          </Button>
                          <Button
                            type="button"
                            color="success"
                            className="w-md me-2"
                            onClick={handleUpdateTimesheet}
                          >
                            Update Timesheet
                          </Button>
                          <Button
                            type="button"
                            color="warning"
                            className="w-md me-2"
                            onClick={handleUpdatePenalty}
                            disabled={!formik.values.updateNonReportPenalty}
                          >
                            Update Penalty
                          </Button>
                          <Button
                            type="button"
                            color="info"
                            className="w-md"
                            onClick={handleValidateSync}
                            disabled={syncLoading}
                          >
                            {syncLoading ? (
                              <>
                                <Spinner size="sm" className="me-1" /> Validating…
                              </>
                            ) : (
                              "Validate Sync"
                            )}
                          </Button>
                        </div>
                      </Col>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {(syncError || syncResult) && (
            <Row className="d-flex justify-content-center">
              <Col xl="10">
                <Card>
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <CardTitle className="mb-0">Attendance Sync Validation</CardTitle>
                      {syncResult ? (
                        <Button
                          type="button"
                          color="success"
                          size="sm"
                          onClick={handleExportExcel}
                        >
                          <i className="mdi mdi-file-excel me-1" />
                          Export to Excel
                        </Button>
                      ) : null}
                    </div>

                    {syncError ? (
                      <div className="alert alert-danger">{syncError}</div>
                    ) : null}

                    {syncResult ? (
                      <>
                        <div className="d-flex flex-wrap gap-2 mb-4">
                          <Badge color="light" className="text-dark border p-2">
                            FlexiTrac Records: <strong>{syncResult.totalFlexiRecords}</strong>
                          </Badge>
                          <Badge color="light" className="text-dark border p-2">
                            ERP Records: <strong>{syncResult.totalERPRecords}</strong>
                          </Badge>
                          <Badge color="success" className="p-2">
                            Matched: {syncResult.matchedCount}
                          </Badge>
                          <Badge color="danger" className="p-2">
                            Missing in ERP: {syncResult.missingInERPCount}
                          </Badge>
                          <Badge color="warning" className="p-2">
                            Missing in FlexiTrac: {syncResult.missingInFlexiTracCount}
                          </Badge>
                          <Badge color="info" className="p-2">
                            Total Hours Mismatch: {syncResult.totalHoursMismatchCount}
                          </Badge>
                          <Badge color="primary" className="p-2">
                            Billable Hours Mismatch: {syncResult.billableHoursMismatchCount}
                          </Badge>
                        </div>

                        {(syncResult.differences || []).length === 0 ? (
                          <div className="text-muted">No differences found — records are in sync.</div>
                        ) : (
                          <div className="table-responsive">
                            <Table bordered hover size="sm" className="mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>Employee Id</th>
                                  <th>Employee Name</th>
                                  <th>Date</th>
                                  <th>Difference</th>
                                  <th className="text-end">Flexi Total</th>
                                  <th className="text-end">ERP Total</th>
                                  <th className="text-end">Total Diff</th>
                                  <th className="text-end">Flexi Billable</th>
                                  <th className="text-end">ERP Billable</th>
                                  <th className="text-end">Billable Diff</th>
                                  <th>Flexi Status</th>
                                  <th>ERP Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {syncResult.differences.map((row, i) => (
                                  <tr key={`${row.employeeId}-${row.date}-${i}`}>
                                    <td>{row.employeeId}</td>
                                    <td>{row.employeeName}</td>
                                    <td>{row.date ? String(row.date).substring(0, 10) : "—"}</td>
                                    <td>
                                      <Badge color={differenceBadgeColor(row.differenceType)}>
                                        {row.differenceType}
                                      </Badge>
                                    </td>
                                    <td className="text-end">{formatHours(row.flexiTotalHours)}</td>
                                    <td className="text-end">{formatHours(row.erpTotalHours)}</td>
                                    <td className="text-end">{formatHours(row.totalHoursDiff)}</td>
                                    <td className="text-end">{formatHours(row.flexiBillableHours)}</td>
                                    <td className="text-end">{formatHours(row.erpBillableHours)}</td>
                                    <td className="text-end">{formatHours(row.billableHoursDiff)}</td>
                                    <td>{row.flexiStatus || "—"}</td>
                                    <td>{row.erpStatus || "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        )}
                      </>
                    ) : null}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};
export default withRouter(AttendanceDetails);
